/**
 * Q2c — N3 · Competência 3 (Geometria) · task-model H3.18.K02
 *
 *   "Dados um triângulo, um eixo de simetria e um segundo triângulo fora do lugar do
 *    outro lado do eixo, ARRASTAR os vértices do segundo até que ele fique simétrico
 *    ao primeiro."
 *
 * É O FORMATO DA QUESTÃO 02 DO GEOGEBRA: o triângulo A′B′C′ já existe desde o início,
 * do lado certo do eixo mas nas posições erradas, e o trabalho é acertá-lo. Muda o que
 * se pede em relação a H3.18.K01 (marcar pontos numa malha vazia): aqui o aluno parte
 * de uma figura pronta e a CORRIGE, comparando cada vértice com o seu par.
 *
 * O PONTO DE PARTIDA NUNCA É A RESPOSTA. Cada vértice começa deslocado de um ou dois
 * quadradinhos da sua posição certa, ainda do outro lado do eixo — perto o bastante
 * para o aluno ver que "é esse o triângulo", longe o bastante para não haver acerto
 * sem mexer.
 *
 * A CORRESPONDÊNCIA IMPORTA. Os nomes A′, B′, C′ dizem qual vértice é imagem de qual; o
 * triângulo certo com os vértices trocados tem diagnóstico próprio.
 */

import type { Rng } from '../../core/rng.ts';
import { sampleWhere } from '../../core/sampling.ts';
import type { Vec2 } from '../../geom/vec.ts';
import type { DifficultySourceCode } from '../../schema/difficulty.ts';
import {
  SIM_CORRESPONDENCIA,
  SIM_DISTANCIA,
  SIM_EIXO_ERRADO,
  SIM_INCOMPLETO,
  SIM_TRANSLACAO,
} from '../../schema/errors.ts';
import type { DragTarget, ItemTemplate } from '../../schema/item.ts';
import { templateId } from '../../schema/item.ts';
import { br, strong, txt, type Inline } from '../../schema/richtext.ts';
import { skillCode, taskModelCode } from '../../schema/taxonomy.ts';
import {
  aneisDeRevisao,
  LEGENDA_ANEIS,
  mesmoConjunto,
  mesmoPonto,
  passoTxt,
} from '../_comum/plano.ts';
import {
  candidato,
  cenario,
  CONTEXTOS,
  eixoTxt,
  errouDistancia,
  lado,
  M,
  NOMES,
  quadro,
  refletiuEmOutroEixo,
  transladou,
  U,
  type Params as ParamsMalha,
} from '../h3-18-K01-simetria-malha/template.ts';

export interface Params extends ParamsMalha {
  /** Onde A′, B′ e C′ começam, em quadradinhos. */
  readonly iniciais: readonly [Vec2, Vec2, Vec2];
}

const NOMES_LINHA = ['A′', 'B′', 'C′'] as const;

const DESLOCAMENTOS: readonly Vec2[] = [
  { x: 1, y: 0 },
  { x: -1, y: 0 },
  { x: 0, y: 1 },
  { x: 0, y: -1 },
  { x: 1, y: 1 },
  { x: -1, y: 1 },
  { x: 1, y: -1 },
  { x: -1, y: -1 },
  { x: 2, y: 0 },
  { x: 0, y: 2 },
  { x: -2, y: 0 },
  { x: 0, y: -2 },
];

const dentro = (p: Vec2): boolean => p.x >= 1 && p.x <= M - 1 && p.y >= 1 && p.y <= M - 1;

/** O triângulo de partida: cada vértice fora do lugar, mas do lado certo do eixo. */
function partida(geo: Omit<ParamsMalha, 'contexto'>, g: Rng): [Vec2, Vec2, Vec2] | null {
  const pts = geo.imagens.map((im) => {
    const d = g.pick(DESLOCAMENTOS);
    return { x: im.x + d.x, y: im.y + d.y };
  }) as [Vec2, Vec2, Vec2];
  const ladoCerto = Math.sign(lado(geo.eixo, geo.imagens[0]));
  if (
    !pts.every(
      (p) =>
        dentro(p) && Math.sign(lado(geo.eixo, p)) === ladoCerto && Math.abs(lado(geo.eixo, p)) >= 1,
    )
  )
    return null;
  // Nenhum vértice pode começar em cima de uma posição certa (a sua ou a de outro).
  if (pts.some((p) => geo.imagens.some((im) => mesmoPonto(p, im)))) return null;
  const [a, b, c] = pts;
  const area2 = Math.abs((b.x - a.x) * (c.y - a.y) - (c.x - a.x) * (b.y - a.y));
  return area2 >= 6 ? pts : null;
}

function drawParams(rng: Rng): Params {
  const contexto = rng.int(0, CONTEXTOS.length - 1);
  const geo = sampleWhere(rng.fork('geometria'), candidato, (c) => c !== null, {
    label: 'triangulo',
    maxTries: 200,
  }) as Omit<ParamsMalha, 'contexto'>;
  const iniciais = sampleWhere(
    rng.fork('partida'),
    (g) => partida(geo, g),
    (c) => c !== null,
    {
      label: 'partida',
      maxTries: 200,
    },
  ) as [Vec2, Vec2, Vec2];
  return { contexto, ...geo, iniciais };
}

const naMalha = (pos: readonly Vec2[]): Vec2[] => pos.map((p) => ({ x: p.x / U, y: p.y / U }));

function alvoDe(p: Params): DragTarget {
  const noLugar = (ms: readonly Vec2[], i: number): boolean => {
    const m = ms[i];
    return m !== undefined && mesmoPonto(m, p.imagens[i] as Vec2);
  };
  const certo = (ms: readonly Vec2[]): boolean =>
    ms.length === 3 && [0, 1, 2].every((i) => noLugar(ms, i));

  return {
    describe: [
      txt(
        'Arraste os vértices A′, B′ e C′ até as posições que tornam o triângulo A′B′C′ simétrico ao triângulo ABC em relação à reta r.',
      ),
    ],

    correto: (pos) => certo(naMalha(pos)),

    diagnostics: [
      {
        // O triângulo certo, com os vértices trocados de nome.
        error: SIM_CORRESPONDENCIA,
        test: (pos) => {
          const ms = naMalha(pos);
          return mesmoConjunto(ms, p.imagens) && !certo(ms);
        },
      },
      { error: SIM_TRANSLACAO, test: (pos) => transladou(p, naMalha(pos)) },
      { error: SIM_EIXO_ERRADO, test: (pos) => refletiuEmOutroEixo(p, naMalha(pos)) },
      { error: SIM_DISTANCIA, test: (pos) => errouDistancia(p, naMalha(pos)) },
      { error: SIM_INCOMPLETO, test: (pos) => [0, 1, 2].some((i) => noLugar(naMalha(pos), i)) },
    ],

    partialCredit: NOMES_LINHA.map((nome, i) => ({
      id: `vertice-${i}`,
      label: `${nome} no lugar certo`,
      weight: 1 / 3,
      test: (pos: readonly Vec2[]) => noLugar(naMalha(pos), i),
    })),

    analyze(pos) {
      const ms = naMalha(pos);
      const out: Inline[] = [];
      const certos = NOMES_LINHA.filter((_n, i) => noLugar(ms, i));
      const parados = ms.every((m, i) => mesmoPonto(m, p.iniciais[i] as Vec2));
      if (parados) out.push(txt('Os vértices ficaram onde começaram: nenhum foi movido.'));
      else if (certos.length === 3)
        out.push(txt('Os três vértices — A′, B′ e C′ — estão no lugar certo.'));
      else if (certos.length === 0) out.push(txt('Nenhum dos vértices está no lugar certo.'));
      else out.push(txt(`Estão no lugar certo: ${certos.join(' e ')}.`));
      if (!certo(ms) && mesmoConjunto(ms, p.imagens)) {
        out.push(
          br,
          txt(
            'O triângulo ocupa a posição certa, mas os vértices estão trocados: cada um tem de ficar em frente ao seu par, do outro lado da reta.',
          ),
        );
      }
      NOMES_LINHA.forEach((nome, i) => {
        if (noLugar(ms, i)) return;
        const v = p.vertices[i] as Vec2;
        const im = p.imagens[i] as Vec2;
        out.push(
          br,
          strong(`${nome}: `),
          txt(
            `de ${NOMES[i]} até ${nome}, anda-se ${passoTxt(im.x - v.x, im.y - v.y)} — o eixo corta esse caminho ao meio, em ângulo reto.`,
          ),
        );
      });
      return out;
    },

    reviewShapes(pos) {
      const ms = naMalha(pos);
      const certosI = [0, 1, 2].filter((i) => noLugar(ms, i));
      const erradosI = [0, 1, 2].filter((i) => !noLugar(ms, i));
      return [
        {
          t: 'poly',
          pts: p.imagens.map((v) => ({ x: v.x * U, y: v.y * U })),
          closed: true,
          role: 'construction',
        },
        ...aneisDeRevisao(
          certosI.map((i) => ms[i] as Vec2),
          erradosI.map((i) => ms[i] as Vec2),
          erradosI.map((i) => p.imagens[i] as Vec2),
          U,
        ),
      ];
    },

    reviewLegend: [
      ...LEGENDA_ANEIS,
      txt(' O contorno tracejado é o triângulo simétrico esperado.'),
    ],
  };
}

export const simetriaArrastar: ItemTemplate<Params, null> = {
  id: templateId('h3-18-K02-simetria-arrastar'),
  version: 1,
  taskModel: taskModelCode('H3.18.K02'),
  skills: [skillCode('H3.18')],
  axis: 'I',
  level: 'N3',
  title: 'Triângulo simétrico: arrastar os vértices',
  // Crédito parcial por vértice no lugar.
  responseModel: 'GPCM',

  difficultySources: ['steps.2'],

  params: drawParams,
  solve: () => null,
  buggy: [],

  variantDifficulty(p) {
    const fontes: DifficultySourceCode[] = [];
    if (p.eixo.o === 'diagonal' || p.eixo.o === 'antidiagonal') fontes.push('steps.3plus');
    return fontes;
  },

  build(p) {
    const ctx = CONTEXTOS[p.contexto] as (typeof CONTEXTOS)[number];
    const diagonal = p.eixo.o === 'diagonal' || p.eixo.o === 'antidiagonal';
    const tela = (v: Vec2): Vec2 => ({ x: v.x * U, y: v.y * U });
    const descricao =
      `Malha quadriculada de ${M} por ${M} quadradinhos, sem numeração. A reta r é ${eixoTxt(p.eixo.o, 'f')}. ` +
      'De um lado dela está o triângulo ABC; do outro, o triângulo A′B′C′, que pode ser mudado arrastando os vértices, ' +
      'que encaixam nos cruzamentos da malha.';

    return {
      stem: [
        txt(ctx.texto),
        br,
        txt(
          'Do outro lado da reta r, um segundo triângulo, A′B′C′, já foi desenhado — mas fora do lugar.',
        ),
      ],
      comando: [
        txt('Arraste os vértices '),
        strong('A′, B′ e C′'),
        txt(
          ' até as posições que tornam o triângulo A′B′C′ simétrico ao triângulo ABC em relação à reta r.',
        ),
      ],
      stimuli: [],
      interaction: {
        kind: 'drag-points',
        descricao,
        backdrop: cenario(p),
        viewBox: quadro(),
        pontos: NOMES_LINHA.map((nome, i) => ({ nome, inicial: tela(p.iniciais[i] as Vec2) })),
        ligacoes: [
          [0, 1],
          [1, 2],
          [2, 0],
        ],
        passo: U,
        alvo: alvoDe(p),
      },
      dicas: [
        [
          txt(
            'Cada vértice e o seu simétrico ficam na mesma perpendicular à reta r, à mesma distância dela, em lados opostos.',
          ),
        ],
        [
          txt(
            'Conte os quadradinhos de A até a reta r, no caminho perpendicular a ela. A′ fica do outro lado, à mesma distância.',
          ),
        ],
      ],
      rationale: [
        strong('Resolução. '),
        txt(
          'Na reflexão em relação a uma reta, cada ponto e sua imagem ficam na mesma perpendicular ao eixo, à mesma ' +
            'distância dele, em lados opostos.',
        ),
        ...p.vertices.flatMap((v, i) => {
          const im = p.imagens[i] as Vec2;
          return [
            br,
            txt(`De ${NOMES[i]} até ${NOMES_LINHA[i]}: ${passoTxt(im.x - v.x, im.y - v.y)}.`),
          ];
        }),
        br,
        txt(
          'O triângulo refletido fica "virado": percorrido na ordem A′, B′, C′, ele gira no sentido oposto ao de ABC.',
        ),
      ],
      declared: diagonal ? 'dificil' : 'facil',
    };
  },
};
